import {
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { FormDataConsumer, SimpleForm, useDataProvider } from 'react-admin';
import { channelFilterInputs } from './ChannelFilterInputs';

export default function ChannelFilterForm({
  source,
  label = 'Channel Filters',
  fullWidth,
  onFiltersChange,
  ...props
}) {
  const [filters, setFilters] = useState({});
  const [expanded, setExpanded] = useState(false);
  const [channelCount, setChannelCount] = useState(null);
  const [loadingCount, setLoadingCount] = useState(false);
  const [currentFormData, setCurrentFormData] = useState({});
  const dataProvider = useDataProvider();
  const previousFormDataRef = useRef({});
  const debounceTimeoutRef = useRef(null);

  const fetchChannelCount = useCallback(async (filterData) => {
    setLoadingCount(true);
    try {
      const result = await dataProvider.getList('channels', {
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'ASC' },
        filter: filterData,
      });
      setChannelCount(result.total);
    } catch (error) {
      console.error('Error fetching channel count:', error);
      setChannelCount(null);
    } finally {
      setLoadingCount(false);
    }
  }, [dataProvider]);

  // Function to handle form data changes with debouncing
  const handleFormDataChange = useCallback((formData) => {
    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set new timeout
    debounceTimeoutRef.current = setTimeout(() => {
      // Remove empty values from form data
      const cleanedFilters = Object.entries(formData || {}).reduce((acc, [key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          acc[key] = value;
        }
        return acc;
      }, {});

      // Update filters state
      setFilters(cleanedFilters);

      // Notify parent component
      if (onFiltersChange) {
        onFiltersChange(cleanedFilters);
      }

      // Fetch channel count if filters exist
      if (Object.keys(cleanedFilters).length > 0) {
        fetchChannelCount(cleanedFilters);
      } else {
        setChannelCount(null);
      }
    }, 500); // 500ms debounce
  }, [onFiltersChange, fetchChannelCount]);

  // Cleanup timeout on unmount
  useEffect(() => () => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
  }, []);

  const handleSubmit = (values) => {
    // This is now just a fallback - real-time updates handle most cases
    setCurrentFormData(values);
  };

  const clearFilters = () => {
    // Clear timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    setFilters({});
    setChannelCount(null);
    setCurrentFormData({});
    previousFormDataRef.current = {};

    if (onFiltersChange) {
      onFiltersChange({});
    }
  };

  const getFilterCount = () => Object.keys(filters).length;

  const getFilterSummary = () => {
    const filterEntries = Object.entries(filters);
    if (filterEntries.length === 0) return 'No filters applied';

    return filterEntries.slice(0, 3).map(([key, value]) => {
      if (Array.isArray(value)) {
        return `${key}: ${value.length} items`;
      }
      return `${key}: ${value}`;
    }).join(', ') + (filterEntries.length > 3 ? '...' : '');
  };

  return (
    <Box className="channel-filter-form" sx={{ width: fullWidth ? '100%' : 'auto' }}>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
        {label}
      </Typography>

      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Accordion expanded={expanded} onChange={(e, isExpanded) => setExpanded(isExpanded)}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1, width: '100%',
            }}
            >
              <FilterIcon color="action" />
              <Typography variant="body2" sx={{ flex: 1 }}>
                Channel Filters
                {getFilterCount() > 0 && (
                  <Chip
                    label={getFilterCount()}
                    size="small"
                    color="primary"
                    sx={{ ml: 1 }}
                  />
                )}
              </Typography>
            </Box>
          </AccordionSummary>

          <AccordionDetails>
            <SimpleForm
              onSubmit={handleSubmit}
              defaultValues={filters}
              toolbar={(
                <Box sx={{
                  display: 'flex', gap: 1, mt: 2,
                }}
                />
              )}
            >
              <FormDataConsumer>
                {({ formData }) => {
                  // Only update if form data actually changed
                  const currentFormDataString = JSON.stringify(formData || {});
                  const previousFormDataString = JSON.stringify(previousFormDataRef.current);

                  if (currentFormDataString !== previousFormDataString) {
                    previousFormDataRef.current = formData || {};
                    handleFormDataChange(formData);
                  }

                  return null;
                }}
              </FormDataConsumer>
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 2,
                mb: 2,
              }}
              >
                {channelFilterInputs}
              </Box>
            </SimpleForm>
          </AccordionDetails>
        </Accordion>
      </Paper>

      {(getFilterCount() > 0 || loadingCount || channelCount !== null) && (
        <Box sx={{ mt: 1 }}>
          {getFilterCount() > 0 && (
            <Typography variant="caption" color="textSecondary">
              Active filters:
              {' '}
              {getFilterSummary()}
            </Typography>
          )}
          {loadingCount ? (
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1, mt: 1,
            }}
            >
              <CircularProgress size={16} />
              <Typography variant="caption" color="textSecondary">
                Counting channels...
              </Typography>
            </Box>
          ) : channelCount !== null && (
            <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5 }}>
              Total channels matching filters:
              {' '}
              <strong>{channelCount}</strong>
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
